import React, { useState, useEffect, useRef } from 'react';
import { Check, CheckCheck, Camera } from 'lucide-react';

// Scripted conversation. type: 'user' | 'bot' | 'typing'
const SCRIPT = [
  { type: 'user', kind: 'photo', text: 'receipt.jpg' },
  { type: 'typing' },
  { type: 'bot', lines: ['📸 Receipt scanned!', '☕ Starbucks · $6.40', '📂 IRS: Meals (Line 24b)', '💾 Saved to your books'] },
  { type: 'user', text: 'spent 45 on gas' },
  { type: 'typing' },
  { type: 'bot', lines: ['✅ Expense added!', '💸 $45.00 · 📁 Gas & Fuel', '📂 IRS: Car & truck expenses (Line 9)', '💰 Est. tax saved: ~$13.50'] },
  { type: 'user', text: 'drove 22 miles to client' },
  { type: 'typing' },
  { type: 'bot', lines: ['🚗 Mileage logged: 22 mi', 'Rate $0.76/mi · Deduction $16.72', '📊 YTD: 22 mi · $16.72'] },
  { type: 'user', text: 'got paid 500 from Upwork' },
  { type: 'typing' },
  { type: 'bot', lines: ['✅ Income added!', '💰 $500.00 · Upwork', 'Nice — logged to your 1099 income 📈'] },
];

const DELAYS = { user: 900, typing: 1100, bot: 1600 };

const WhatsAppDemo = () => {
  const [visible, setVisible] = useState([]);
  const bodyRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    let i = 0;
    const step = () => {
      const item = SCRIPT[i];
      if (item.type === 'typing') {
        setVisible((prev) => [...prev, { type: 'typing', id: `t${i}` }]);
      } else {
        setVisible((prev) => {
          const cleaned = prev.filter((m) => m.type !== 'typing');
          return [...cleaned, { ...item, id: `m${i}` }];
        });
      }
      i += 1;
      if (i >= SCRIPT.length) {
        timer.current = setTimeout(() => {
          setVisible([]);
          i = 0;
          timer.current = setTimeout(step, 800);
        }, 3200);
        return;
      }
      timer.current = setTimeout(step, DELAYS[item.type] || 1200);
    };
    timer.current = setTimeout(step, 500);
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [visible]);

  return (
    <div className="relative mx-auto w-full max-w-sm" data-testid="whatsapp-demo">
      {/* Phone frame */}
      <div className="relative bg-black rounded-[2.5rem] p-2.5 shadow-2xl">
        <div className="bg-[#ECE5DD] rounded-[2rem] overflow-hidden">
          {/* Header */}
          <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#84CC16] flex items-center justify-center font-bold text-[#064E3B]">M</div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm leading-tight">MoneyFeed</p>
              <p className="text-[#B2DFDB] text-xs">online</p>
            </div>
          </div>
          {/* Chat body */}
          <div
            ref={bodyRef}
            className="px-3 py-4 space-y-2 h-[420px] overflow-hidden"
            style={{ backgroundImage: 'radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px)', backgroundSize: '16px 16px' }}
          >
            {visible.map((m) => {
              if (m.type === 'typing') {
                return (
                  <div key={m.id} className="flex justify-start animate-[fadeInUp_0.3s_ease]">
                    <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex gap-1">
                      <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                );
              }
              const isUser = m.type === 'user';
              return (
                <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-[fadeInUp_0.35s_ease]`}>
                  <div className={`max-w-[80%] px-3 py-2 shadow-sm text-sm ${isUser ? 'bg-[#DCF8C6] rounded-2xl rounded-tr-sm text-gray-800' : 'bg-white rounded-2xl rounded-tl-sm text-gray-800'}`}>
                    {isUser ? (
                      m.kind === 'photo' ? (
                        <span className="flex flex-col gap-1">
                          <span className="flex flex-col items-center justify-center gap-1 w-36 h-24 bg-white/70 rounded-lg border border-gray-200 text-gray-500">
                            <Camera className="h-6 w-6" />
                            <span className="text-[11px]">{m.text}</span>
                          </span>
                          <span className="flex justify-end"><CheckCheck className="h-3.5 w-3.5 text-[#34B7F1]" /></span>
                        </span>
                      ) : (
                        <span className="flex items-end gap-1">
                          {m.text}
                          <CheckCheck className="h-3.5 w-3.5 text-[#34B7F1] flex-shrink-0" />
                        </span>
                      )
                    ) : (
                      <div className="space-y-0.5">
                        {m.lines.map((l, idx) => (
                          <p key={idx} className={idx === 0 ? 'font-semibold' : ''}>{l}</p>
                        ))}
                        <span className="flex justify-end"><Check className="h-3 w-3 text-gray-400" /></span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Input bar (decorative) */}
          <div className="bg-[#ECE5DD] px-3 py-2 flex items-center gap-2">
            <div className="flex-1 bg-white rounded-full px-4 py-2 text-gray-400 text-sm">Message MoneyFeed…</div>
            <div className="h-9 w-9 rounded-full bg-[#075E54] flex items-center justify-center text-white">➤</div>
          </div>
        </div>
      </div>
      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

export default WhatsAppDemo;
